import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

const PrivacyPage: React.FC = () => {
  const navigate = useNavigate();
  
  return (
    <div className="min-h-screen bg-[#161513] py-12 px-4 sm:px-6">
      {/* Logo Background */}
      <div 
        className="absolute top-0 left-0 right-0 h-[400px] bg-cover bg-center bg-no-repeat opacity-20 z-0 pointer-events-none"
        style={{ backgroundImage: `url('/src/assets/logo.gif')` }}
      />
      
      <div className="max-w-4xl mx-auto relative z-10">
        <div className="flex items-center mb-8">
          <button 
            onClick={() => navigate('/')} 
            className="flex items-center text-gray-300 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Home
          </button>
        </div>
        
        <div className="bg-[#161513] rounded-xl shadow-lg border border-[#0CB4E4]/30 overflow-hidden">
          <div className="p-6 md:p-8 border-b border-[#0CB4E4]/20">
            <h1 className="text-3xl font-bold text-white">Privacy Policy for cr3ate.ai</h1>
            <p className="text-gray-300 mt-2">Effective Date: November 1, 2025</p>
            <p className="text-gray-300 mt-4">
              This Privacy Policy describes how <strong>Ydrive Inc.</strong>, a Delaware C Corporation 
              ("<strong>Ydrive</strong>," "<strong>we</strong>," "<strong>us</strong>," or "<strong>our</strong>"), 
              collects, uses, shares, and protects your personal information when you access or use{' '}
              <strong>cr3ate.ai</strong>, including our website, mobile apps (iOS and Android), 
              and Unreal Engine plugin (collectively, the "<strong>Service</strong>").
            </p>
            <p className="text-gray-300 mt-3">
              By using the Service, you agree to this Privacy Policy. If you are located in the EU, UK, 
              or Switzerland, please refer to our{' '}
              <Link to="/privacy/gdpr" className="text-[#0CB4E4] hover:text-[#0CB4E4]/80">
                GDPR Addendum
              </Link>
              {' '}for your specific rights.
            </p>
          </div>
          
          <div className="p-6 md:p-8 space-y-6">
            {/* Section 1 */}
            <section>
              <h2 className="text-xl font-semibold text-white mb-3">1. Information We Collect</h2>
              <ul className="list-disc pl-5 text-gray-300 space-y-2">
                <li>Personal information you provide (name, email, password, subscription details)</li>
                <li>Uploaded content and metadata (images, videos for reconstruction)</li>
                <li>Device and usage data (IP, browser, OS, crash logs, performance data)</li>
                <li>Mobile permissions (camera, storage, network, optional location)</li>
              </ul>
            </section>

            {/* Section 2 */}
            <section>
              <h2 className="text-xl font-semibold text-white mb-3">2. How We Use Your Information</h2>
              <ul className="list-disc pl-5 text-gray-300 space-y-2">
                <li>Operate, maintain, and improve the Service</li>
                <li>Process accounts, payments, and support requests</li>
                <li>Enhance accuracy, performance, and model quality</li>
                <li>Conduct R&amp;D including de-identified model training</li>
                <li>Comply with applicable laws and security requirements</li>
              </ul>
              <p className="text-gray-300 mt-3">
                User content and reconstructions may be used internally for improvement and model training 
                as permitted under applicable law.
              </p>
            </section>

            {/* Section 3 */}
            <section>
              <h2 className="text-xl font-semibold text-white mb-3">3. Sharing and Disclosure</h2>
              <p className="text-gray-300">
                We do not sell your personal data. We may share with:
              </p>
              <ul className="list-disc pl-5 text-gray-300 space-y-2 mt-2">
                <li>Service providers (hosting, analytics, payments)</li>
                <li>Authorized partners or collaborators (when you opt in)</li>
                <li>Affiliates and subsidiaries within Ydrive</li>
                <li>Successors (in merger or acquisition)</li>
                <li>Legal authorities where required by law</li>
              </ul>
              <p className="text-gray-300 mt-3">
                Public reconstructions may be used for educational or promotional purposes without 
                further consent, to the extent permitted by law.
              </p>
            </section>

            {/* Section 4 */}
            <section>
              <h2 className="text-xl font-semibold text-white mb-3">4. Your Rights</h2>
              <p className="text-gray-300">
                Depending on your jurisdiction, you may have rights to access, correct, delete, 
                or object to data processing. For requests, contact us at{' '}
                <a href="mailto:privacy@cr3ate.ai" className="text-[#0CB4E4] hover:text-[#0CB4E4]/80">
                  privacy@cr3ate.ai
                </a>
              </p>
            </section>

            {/* Section 5 */}
            <section>
              <h2 className="text-xl font-semibold text-white mb-3">5. Security</h2>
              <p className="text-gray-300">
                We implement encrypted storage, secure access controls, and safeguards against 
                unauthorized access. However, no system is 100% secure.
              </p>
            </section>

            {/* Section 6 */}
            <section>
              <h2 className="text-xl font-semibold text-white mb-3">6. International Users</h2>
              <p className="text-gray-300">
                We operate in the U.S. and may transfer your data globally. By using the Service, 
                you consent to such transfers.
              </p>
            </section>

            {/* Section 7 */}
            <section>
              <h2 className="text-xl font-semibold text-white mb-3">7. Children's Privacy</h2>
              <p className="text-gray-300">
                We do not knowingly collect data from children under 13 (or age of consent per jurisdiction).
              </p>
            </section>

            {/* Section 8 */}
            <section>
              <h2 className="text-xl font-semibold text-white mb-3">8. Updates</h2>
              <p className="text-gray-300">
                We may update this Privacy Policy periodically. Continued use constitutes acceptance of updates.
              </p>
            </section>
          </div>
          
          <div className="p-6 md:p-8 bg-[#161513]/70 border-t border-[#0CB4E4]/20">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex flex-wrap gap-4">
                <Link to="/terms" className="text-[#0CB4E4] hover:text-[#0CB4E4]/80">
                  View Terms of Service
                </Link>
              </div>
              
              <button 
                onClick={() => navigate('/')} 
                className="px-5 py-2.5 bg-[#0C5484] text-white hover:bg-[#0CB4E4]/90 transition-colors shadow-md rounded-lg"
              >
                Back to Home
              </button>
            </div>
          </div>
        </div>

        <footer className="text-center text-gray-400 mt-8">
          <p>© Ydrive Inc.</p>
        </footer>
      </div>
    </div>
  );
};

export default PrivacyPage;